import * as React from 'react';
import { useContext, useMemo, useCallback } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { useComputed, useSignal } from '@preact/signals-react';
import styled from 'styled-components';
import produce from 'immer';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
  HtmlTransformKind,
} from '@/webapi/use-experience-api';
import {
  ContextualAttachment,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';
import { VSpace } from '@/components/spacing';
import {
  AiComponentContextProps,
  AiComponentContextProvider,
  CodeBlockMutationOptions,
} from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { AiEditorTabContainer } from '@/features/editor/widgets/shared/ai-editing-layout/tab-container';
import { DeviceType } from '@/utils/definitions';
import { EditorContext } from '@/features/editor/context/editor-context';
import { AiOriginAction } from '@/pkg/sdk';
import {
  extractCSSAttachment,
  extractHTMLAttachment,
} from '@/features/editor/widgets/ai-section-editor/extraction';
import { AiPromptMessage } from '@/features/editor/context/use-device-preview';
import { AccountContext } from '@/features/account-context';

export interface AiSectionEditorProps {
  selector: string;
  widgetId?: string;
  loomiId?: string;
  editorId?: string;
  originalChange?: EditorDeclarativeBlock;

  initialMessage?: AiPromptMessage;
}

export function AiSectionEditor({
  selector,
  originalChange,
  editorId,
  initialMessage,
}: AiSectionEditorProps) {
  useSignals();

  const {
    account: {
      store: { defaultAiModels },
    },
  } = useContext(AccountContext);

  const {
    devicePreview: {
      editorState: { device },
      runInIframe,
    },
    transpiler: { asCompoundChange },
  } = useContext(EditorContext);

  const initChange = () =>
    asCompoundChange(
      EditorChangeKind.EDIT_COMPOUND,
      HtmlTransformKind.APPEND_AFTER,
      selector,
      selector,
      ``,
      ``,
      ``,
      editorId,
    );

  const newChange = useSignal(originalChange || initChange());

  const ctxAttachments: ContextualAttachment[] = useMemo(() => {
    const html = extractHTMLAttachment(selector, runInIframe);
    const css = extractCSSAttachment(selector, runInIframe);
    return [html, css];
  }, [selector]);

  const onCodeBlockMutation = (opts: CodeBlockMutationOptions) => {
    const { html, css, js, block } = opts;
    if (html) {
      block.html = html;
    }
    if (css) {
      block.css = css;
    }
    if (js) {
      block.js = js;
    }
  };

  const onEditAttachment = useCallback(
    (
      attachmentsSignal: any,
      oldAttachment: ContextualAttachment,
      newSelector: string,
    ) => {
      try {
        const newHtmlAttachment = extractHTMLAttachment(
          newSelector,
          runInIframe,
          oldAttachment.editable,
        );
        const newCssAttachment = extractCSSAttachment(
          newSelector,
          runInIframe,
          false,
        );

        // Find the old HTML and CSS attachments
        const oldHtmlId = oldAttachment.id.endsWith(`-css`)
          ? oldAttachment.id.slice(0, -4)
          : oldAttachment.id;
        const oldCssId = `${oldHtmlId}-css`;

        // Update the attachments signal directly
        attachmentsSignal.value = attachmentsSignal.value.map(
          (att: ContextualAttachment) => {
            if (att.id === oldHtmlId) {
              return newHtmlAttachment;
            }
            if (att.id === oldCssId) {
              return newCssAttachment;
            }
            return att;
          },
        );

        const blockSelector = newChange.value.block.selector;
        if (
          oldAttachment.selector &&
          oldAttachment.selector === blockSelector
        ) {
          newChange.value = produce(newChange.value, (draft) => {
            draft.editorSelector = draft.editorSelector?.replace(
              blockSelector,
              oldAttachment.selector,
            );
            draft.block.selector = blockSelector;
          });
        }
      } catch (error) {
        console.error(`Failed to re-extract attachment:`, error);
      }
    },
    [runInIframe, newChange],
  );

  const isComponentReadyOverride = (state: any) => {
    if (state.isProcessing.value) return false;
    const block = state.newChange.value.block.value;
    return (
      (block.css?.length > 5 || block.js?.length > 5) && !!block.aiSessionId
    );
  };

  const props = useComputed(
    () =>
      ({
        originalChange: newChange.value,
        selectedAgent: {
          id:
            defaultAiModels?.[`section-modifier`] ||
            `section-modifier-gemini-3.7-flash`,
          type: `agent`,
        } as SelectedAgent,
        originAction: AiOriginAction.SECTION_MODIFIER,
        starters: () => null,
        initialContextualAttachments: ctxAttachments,
        onCodeBlockMutation,
        onEditAttachment,
        isComponentReadyOverride,
        placeholder: `Tell us what you want to change`,
        initialMessage,
      } as AiComponentContextProps),
  );

  return (
    <Wrapper device={device}>
      <VSpace value={2} />

      <AiComponentContextProvider
        key="ai-section-editor-ctx-provider"
        props={props}
      >
        <AiEditorTabContainer />
      </AiComponentContextProvider>
      <VSpace value={2} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `100%`};

  display: flex;
  flex-direction: column;
`;
